/**
 * PhotoViewer — Bộ soi ảnh dùng chung của mọi màn có ảnh (spec 2026-10-28-bo-soi-anh-chuyen-nghiep,
 * CEO chốt 1b · 2a · 3b · 4c 28/10/2026).
 *
 * - Ảnh luôn đủ nét: chọn bậc ảnh theo cỡ đang bày × độ nét màn hình, phóng tới đâu tải bậc nét
 *   tới đó, tới tệp gốc (`useLadderImage`). Bậc cũ giữ nguyên tới khi bậc mới tải xong.
 * - Phóng tới 800% ảnh gốc: lăn chuột / chụm hai ngón quanh con trỏ, kéo, chạm đúp; kính lúp (giữ
 *   Z / chạm giữ); bản đồ nhỏ; toàn màn; nền soi; phím tắt (`useGestures`).
 * - Có `compare` thì so hai bản: cạnh nhau (phóng chung), thanh gạt, nháy (giữ B), khác biệt.
 * - Công cụ: biểu đồ sáng, báo cháy / mất đen, ống hút màu, lưới căn, xoay / lật để soi.
 *
 * `view` + `onViewChange` cho phép nhiều bộ soi dùng CHUNG một mức phóng (lưới nhiều tấm).
 * `overlay` nằm TRONG lớp phóng (nét khoanh của khách co giãn theo ảnh); dòng báo nằm NGOÀI.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { neededLongEdge, pickRung, type PhotoLadder } from "./ladder";
import { nextRotation, orientationCss, rotatedSize, toImagePoint, UPRIGHT, type Orientation } from "./orientation";
import { GridOverlay, LadderImages, Loupe, MiniMap, PaneLabel, WipeHandle } from "./parts";
import { cropPixels, histogram, sampleColor } from "./pixels";
import { ToolsPanel } from "./ToolsPanel";
import {
  COMPARE_MODES,
  FIT_VIEW,
  MODE_LABEL,
  NO_TOOLS,
  type ColorPin,
  type CompareMode,
  type ToolFlags,
  type ViewerBg,
  type ViewState,
} from "./types";
import { useViewerGestures } from "./useGestures";
import { useLadderImage } from "./useLadderImage";
import { useClippingOverlay, useDiffOverlay, type DiffState } from "./useOverlays";
import { usePixelReader } from "./usePixelReader";
import { BG_CLASS, readBg, useBoxSize, useReference, writeBg } from "./viewer-hooks";
import { HelpPanel, ViewerToolbar } from "./ViewerToolbar";
import { fitScale, nextStep, placeImage, VIEWER_MAX, visibleRect } from "./viewport";

type Props = {
  ladder: PhotoLadder;
  alt: string;
  /** Bản thứ hai để so (thường là ảnh gốc khi `ladder` là bản sửa). */
  compare?: { ladder: PhotoLadder; label: string; ownLabel: string } | undefined;
  defaultMode?: CompareMode | undefined;
  view?: ViewState | undefined;
  onViewChange?: ((v: ViewState) => void) | undefined;
  overlay?: ReactNode;
  className?: string | undefined;
  /** "full" mọi nút; "compact" gọn cho khung nhỏ; "none" không thanh nút (lưới nhiều tấm). */
  toolbar?: "full" | "compact" | "none" | undefined;
  /** Ảnh đang vừa khung thì lăn chuột cuộn trang như cũ (giữ Ctrl / chụm để phóng). */
  pageScrollWhenFit?: boolean | undefined;
  autoFocus?: boolean | undefined;
};

/** Ngưỡng tỉ lệ × độ nét màn hình từ đó vẽ điểm ảnh vuông thay vì làm mịn — soi điểm như Photoshop. */
const PIXELATE_AT = 3;

function diffNote(d: DiffState): string | null {
  if (d.status === "loading") return "Đang so hai bản…";
  if (d.status === "blocked") return d.reason;
  if (d.status === "ready") return `Khác nhau ${d.percent.toFixed(1)}% điểm ảnh (tô đỏ)`;
  return null;
}

export function PhotoViewer(props: Props) {
  const { ladder, alt, compare, overlay, className, toolbar = "full", pageScrollWhenFit = false } = props;
  const rootRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const box = useBoxSize(stageRef);

  const [ownView, setOwnView] = useState<ViewState>(FIT_VIEW);
  const view = props.view ?? ownView;
  const setView = (v: ViewState) => {
    if (!props.view) setOwnView(v);
    props.onViewChange?.(v);
  };

  const [orientation, setOrientation] = useState<Orientation>(UPRIGHT);
  const [mode, setMode] = useState<CompareMode>(props.defaultMode ?? "side");
  const activeMode: CompareMode = compare ? (mode === "single" ? "side" : mode) : "single";
  const [flickerHeld, setFlickerHeld] = useState(false);
  const [wipe, setWipe] = useState(0.5);
  const [loupeOn, setLoupeOn] = useState(false);
  const [loupeKey, setLoupeKey] = useState(false);
  const [tools, setTools] = useState<ToolFlags>(NO_TOOLS);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [pins, setPins] = useState<ColorPin[]>([]);
  const [bg, setBg] = useState<ViewerBg>(readBg);
  const [fs, setFs] = useState<"none" | "native" | "css">("none");

  // ---- mốc 100% + tỉ lệ + bậc ảnh cần
  const A = useReference(ladder);
  const B = useReference(compare?.ladder ?? { rungs: [] });
  const refA = A.ref;
  const refB = B.ref ?? refA;
  const shown = refA ? rotatedSize(refA, orientation) : undefined;
  const fit = shown ? fitScale(shown, box) : 1;
  const scale = view.zoom === "fit" ? fit : view.zoom;
  const zoomed = view.zoom !== "fit";
  const dpr = typeof window !== "undefined" && window.devicePixelRatio > 0 ? window.devicePixelRatio : 1;
  const needed = refA ? Math.ceil(Math.max(refA.w, refA.h) * scale * dpr) : neededLongEdge(box, undefined, "fit", dpr);
  const scaleB = refA && refB ? scale * (Math.max(refA.w, refA.h) / Math.max(refB.w, refB.h)) : scale;
  const neededB = refB ? Math.ceil(Math.max(refB.w, refB.h) * scaleB * dpr) : needed;
  const liA = useLadderImage(ladder, box.w > 0 ? needed : 0);
  const liB = useLadderImage(compare?.ladder ?? { rungs: [] }, compare && box.w > 0 ? neededB : 0);
  const pixelated = scale * dpr >= PIXELATE_AT;
  const upright = orientation.rotate === 0 && !orientation.flip;

  // ---- đọc điểm ảnh (chỉ khi bật công cụ)
  const readUrl = ladder.rungs.filter((r) => !r.isOriginal).at(-1)?.url ?? ladder.rungs[0]?.url;
  const pixels = usePixelReader(readUrl, tools.histogram || tools.clipping || tools.eyedropper);
  const blocked = pixels.status === "blocked" ? pixels.reason : undefined;
  const clipUrl = useClippingOverlay(pixels, tools.clipping);
  const readUrlB = compare?.ladder.rungs.filter((r) => !r.isOriginal).at(-1)?.url;
  const diff = useDiffOverlay(readUrl, readUrlB, activeMode === "diff");
  const rect = shown ? visibleRect(view.pan, scale, box, shown) : { x: 0, y: 0, w: 1, h: 1 };
  const histRect = upright ? rect : { x: 0, y: 0, w: 1, h: 1 };
  const rx = Math.round(histRect.x * 200);
  const ry = Math.round(histRect.y * 200);
  const rw = Math.round(histRect.w * 200);
  const rh = Math.round(histRect.h * 200);
  const hist = useMemo(() => {
    if (!tools.histogram || pixels.status !== "ready") return null;
    const { data, w, h } = pixels.pixels;
    // Làm tròn vùng về 1/200 ảnh: kéo một chút không đáng tính lại biểu đồ.
    return histogram(cropPixels(data, w, h, { x: rx / 200, y: ry / 200, w: rw / 200, h: rh / 200 }));
  }, [tools.histogram, pixels, rx, ry, rw, rh]);

  const imagePoint = (pt: { x: number; y: number }) => {
    if (!shown) return null;
    const p = placeImage(view.pan, scale, box, shown);
    const u = (pt.x - p.left) / p.width;
    const v = (pt.y - p.top) / p.height;
    if (u < 0 || u > 1 || v < 0 || v > 1) return null;
    return toImagePoint(u, v, orientation);
  };
  const colorAt = (pt: { x: number; y: number }): ColorPin | null => {
    if (pixels.status !== "ready") return null;
    const ip = imagePoint(pt);
    if (!ip) return null;
    const { data, w, h } = pixels.pixels;
    return sampleColor(data, w, h, ip.x * (w - 1), ip.y * (h - 1));
  };

  const g = useViewerGestures(rootRef, {
    view,
    scale,
    fit,
    box,
    shown,
    upright,
    pageScrollWhenFit,
    setView,
    onTap: (pt) => {
      if (!tools.eyedropper) return false;
      const c = colorAt(pt);
      if (c) setPins((p) => [...p, c].slice(-3));
      return true;
    },
  });

  // ---- toàn màn (trình duyệt không cho thì phủ kín bằng CSS — iPhone)
  useEffect(() => {
    const onChange = () =>
      setFs((cur) => (document.fullscreenElement === rootRef.current ? "native" : cur === "native" ? "none" : cur));
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);
  const toggleFullscreen = () => {
    if (fs === "native") return void document.exitFullscreen?.().catch(() => setFs("none"));
    if (fs === "css") return setFs("none");
    const el = rootRef.current;
    if (el?.requestFullscreen) el.requestFullscreen().catch(() => setFs("css"));
    else setFs("css");
  };

  useEffect(() => {
    if (props.autoFocus) rootRef.current?.focus({ preventScroll: true });
  }, [props.autoFocus]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    const t = e.target as HTMLElement;
    if (t.tagName === "INPUT" || t.tagName === "SELECT" || t.tagName === "TEXTAREA") return;
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    const k = e.key;
    if (k === "+" || k === "=") g.zoomTo(nextStep(scale, 1, fit));
    else if (k === "-" || k === "_") g.zoomTo(nextStep(scale, -1, fit));
    else if (k === "0") g.zoomTo("fit");
    else if (k === "1") g.zoomTo(1);
    else if (k === "z" || k === "Z") setLoupeKey(true);
    else if ((k === "b" || k === "B") && compare) setFlickerHeld(true);
    else if (k === "f" || k === "F") toggleFullscreen();
    else if (k === "?") setHelpOpen((v) => !v);
    else return;
    e.preventDefault();
    e.stopPropagation();
  };
  const onKeyUp = (e: React.KeyboardEvent) => {
    if (e.key === "z" || e.key === "Z") setLoupeKey(false);
    if (e.key === "b" || e.key === "B") setFlickerHeld(false);
  };

  // ---- một lớp ảnh (A = bản chính, B = bản so)
  const layer = (which: "A" | "B", withOverlays: boolean) => {
    const ref = which === "A" ? refA : refB;
    const s = which === "A" ? scale : scaleB;
    const li = which === "A" ? liA : liB;
    const measure = which === "A" ? A.measure : B.measure;
    const lbl = which === "A" ? alt : (compare?.label ?? alt);
    if (!ref || box.w <= 0) {
      // Chưa biết kích thước: bày vừa khung để ảnh tải và đo được. Lớp phủ của chỗ gọi (nét khách
      // khoanh) vẫn hiện — `AnnotationLayer` tự đo vùng ảnh `object-contain`.
      return (
        <div className="absolute inset-0 [&>img]:object-contain">
          <LadderImages li={li} alt={lbl} onMeasure={measure} pixelated={false} />
          {withOverlays ? overlay : null}
        </div>
      );
    }
    const outer = placeImage(view.pan, s, box, rotatedSize(ref, orientation));
    const iw = ref.w * s;
    const ih = ref.h * s;
    return (
      <div
        className="absolute"
        style={{ left: outer.left, top: outer.top, width: outer.width, height: outer.height }}
        data-testid={`pv-layer-${which}`}
      >
        <div
          className="absolute"
          style={{
            left: (outer.width - iw) / 2,
            top: (outer.height - ih) / 2,
            width: iw,
            height: ih,
            transform: orientationCss(orientation),
          }}
        >
          <LadderImages li={li} alt={lbl} onMeasure={measure} pixelated={pixelated} />
          {withOverlays && clipUrl ? (
            <img src={clipUrl} alt="" aria-hidden className="pointer-events-none absolute inset-0 h-full w-full" />
          ) : null}
          {withOverlays && activeMode === "diff" && diff.status === "ready" ? (
            <img src={diff.url} alt="" aria-hidden className="pointer-events-none absolute inset-0 h-full w-full" />
          ) : null}
          {withOverlays && tools.grid ? <GridOverlay /> : null}
          {withOverlays ? overlay : null}
        </div>
      </div>
    );
  };

  const notes = [
    liA.note,
    compare && activeMode !== "diff" ? liB.note : null,
    activeMode === "diff" ? diffNote(diff) : null,
  ].filter((n): n is string => !!n);

  const loupeActive = (loupeOn || loupeKey || g.longPress) && upright && !!refA;
  const loupeUrl = pickRung(ladder, Number.POSITIVE_INFINITY)?.url;
  const hoverPoint = g.hover ? imagePoint(g.hover) : null;
  const eyeColor = tools.eyedropper && g.hover ? colorAt(g.hover) : null;
  const bgClass = BG_CLASS[bg === "auto" && fs !== "none" ? "gray" : bg];
  const stageStyle = { touchAction: zoomed ? "none" : "pan-y" } as const;
  const cursor = tools.eyedropper ? "cursor-crosshair" : zoomed ? "cursor-grab active:cursor-grabbing" : "cursor-zoom-in";
  const showB = activeMode === "flicker" && flickerHeld;
  const stage = (refProp: boolean, cls: string, children: ReactNode) => (
    <div
      ref={refProp ? stageRef : undefined}
      data-pv-stage
      className={cn(cls, cursor)}
      style={stageStyle}
      {...g.stageHandlers}
    >
      {children}
    </div>
  );

  return (
    <div
      ref={rootRef}
      role="group"
      aria-label={`Bộ soi ảnh: ${alt}`}
      tabIndex={0}
      data-testid="photo-viewer"
      data-zoom={view.zoom === "fit" ? "fit" : String(view.zoom)}
      data-rung={liA.shown?.label ?? ""}
      className={cn(
        "relative h-full w-full overflow-hidden outline-none focus-visible:ring-2 focus-visible:ring-[var(--gn-gold,#c9a24a)]",
        bgClass,
        fs === "css" && "fixed inset-0 z-[10002] bg-[#767676]",
        className,
      )}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
      onClick={(e) => e.stopPropagation()}
      // Đang phóng / chụm thì kéo là để soi — không cho hộp xem ảnh phía ngoài hiểu thành vuốt chuyển ảnh.
      onTouchStart={(e) => {
        if (zoomed || e.touches.length > 1) e.stopPropagation();
      }}
      onTouchEnd={(e) => {
        if (zoomed || e.changedTouches.length > 1) e.stopPropagation();
      }}
    >
      {activeMode === "side" && compare ? (
        <div className="grid h-full w-full grid-cols-2 gap-1">
          {stage(true, "relative overflow-hidden", (
            <>
              {layer("B", false)}
              <PaneLabel>{compare.label}</PaneLabel>
            </>
          ))}
          {stage(false, "relative overflow-hidden", (
            <>
              {layer("A", true)}
              <PaneLabel>{compare.ownLabel}</PaneLabel>
            </>
          ))}
        </div>
      ) : (
        stage(true, "absolute inset-0", (
          <>
            {activeMode === "flicker" && compare ? (
              <>
                <div className={cn("absolute inset-0", showB && "invisible")}>{layer("A", true)}</div>
                <div className={cn("absolute inset-0", !showB && "invisible")}>{layer("B", false)}</div>
                <PaneLabel>{showB ? compare.label : `${compare.ownLabel} · giữ B để xem ${compare.label.toLowerCase()}`}</PaneLabel>
              </>
            ) : (
              layer("A", true)
            )}
            {activeMode === "wipe" && compare ? (
              <>
                <div className="pointer-events-none absolute inset-0" style={{ clipPath: `inset(0 ${Math.round((1 - wipe) * 1000) / 10}% 0 0)` }}>
                  {layer("B", false)}
                </div>
                <WipeHandle pos={wipe} onPos={setWipe} />
                <PaneLabel>{compare.label}</PaneLabel>
                <PaneLabel right>{compare.ownLabel}</PaneLabel>
              </>
            ) : null}
            {activeMode === "diff" && compare ? (
              <PaneLabel>{`${MODE_LABEL.diff}: ${compare.ownLabel} so với ${compare.label}`}</PaneLabel>
            ) : null}
          </>
        ))
      )}

      {notes.length > 0 ? (
        <div className="pointer-events-none absolute left-2 top-9 z-10 flex max-w-[calc(100%-1rem)] flex-col items-start gap-1">
          {notes.map((n) => (
            <span key={n} role="status" className="rounded bg-black/65 px-2 py-0.5 text-xs text-white">
              {n}
            </span>
          ))}
        </div>
      ) : null}

      {loupeActive && g.hover && hoverPoint && refA && loupeUrl ? (
        <Loupe url={loupeUrl} point={hoverPoint} at={g.hover} size={refA} magnify={scale < 1 ? 1 : Math.min(VIEWER_MAX, scale * 2)} />
      ) : null}

      {eyeColor && g.hover ? (
        <div
          className="pointer-events-none absolute z-20 flex items-center gap-1 rounded bg-black/80 px-2 py-1 text-xs tabular-nums text-white"
          style={{ left: Math.max(4, Math.min(g.hover.x + 14, box.w - 190)), top: Math.max(g.hover.y - 34, 4) }}
          role="status"
        >
          <span className="h-3.5 w-3.5 rounded-sm border border-white/50" style={{ background: eyeColor.hex }} aria-hidden />
          {eyeColor.hex} · R{eyeColor.r} G{eyeColor.g} B{eyeColor.b}
        </div>
      ) : null}

      {zoomed && shown && upright && ladder.rungs[0] && activeMode !== "side" ? (
        <MiniMap url={ladder.rungs[0].url} size={shown} rect={rect} onPan={(pan) => setView({ zoom: view.zoom, pan })} />
      ) : null}

      {toolsOpen ? (
        <ToolsPanel
          tools={tools}
          onTools={setTools}
          blocked={blocked}
          hist={hist}
          pins={pins}
          onClearPins={() => setPins([])}
          orientation={orientation}
          onRotate={() => setOrientation((o) => ({ ...o, rotate: nextRotation(o.rotate) }))}
          onFlip={() => setOrientation((o) => ({ ...o, flip: !o.flip }))}
          onClose={() => setToolsOpen(false)}
        />
      ) : null}
      {helpOpen ? <HelpPanel onClose={() => setHelpOpen(false)} /> : null}

      {toolbar !== "none" ? (
        <ViewerToolbar
          compact={toolbar === "compact" || box.w < 560}
          percentLabel={`${Math.round(scale * 100)}%${ladder.natural ? "" : " bản xem"}`}
          onZoomIn={() => g.zoomTo(nextStep(scale, 1, fit))}
          onZoomOut={() => g.zoomTo(nextStep(scale, -1, fit))}
          onFit={() => g.zoomTo("fit")}
          onActual={() => g.zoomTo(1)}
          loupeOn={loupeOn}
          onToggleLoupe={() => setLoupeOn((v) => !v)}
          loupeDisabled={upright ? undefined : "Bỏ xoay / lật để dùng kính lúp"}
          modes={compare ? COMPARE_MODES : undefined}
          mode={activeMode}
          onMode={setMode}
          onFlickerHold={setFlickerHeld}
          fullscreen={fs !== "none"}
          onFullscreen={toggleFullscreen}
          bg={bg}
          onBg={(v) => {
            setBg(v);
            writeBg(v);
          }}
          toolsOpen={toolsOpen}
          onToggleTools={() => {
            setToolsOpen((v) => !v);
            setHelpOpen(false);
          }}
          helpOpen={helpOpen}
          onToggleHelp={() => {
            setHelpOpen((v) => !v);
            setToolsOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}
